import { getPartialDateGivenMonthsAgo } from 'neuro-utils';
import { TPlatformHandler } from '.';
import { getDiagnosisQueryString } from '../utils';
import { formatStringArray, partialDateToString } from '../utils/queryFormatters';

export const handleMsField: TPlatformHandler = (field, state) => {
  const f = field;

  const docType: string | undefined = (
    {
      typeOfDisease: 'diagnosis',
      numberOfRelapses: 'relapse',
      edss: 'neurologicalStatusAndEDSS',
      edssIncreasedDuringLast24Months: 'neurologicalStatusAndEDSS',
      edssIncreasedDuringLast12Months: 'neurologicalStatusAndEDSS',
    } as Record<string, string>
  )[field];

  let query: string | undefined = undefined;
  switch (field) {
    case 'typeOfDisease':
      query = '';
      if (
        state.typeOfDisease !== 'noCropping' &&
        Array.isArray(state.typeOfDisease) &&
        state.typeOfDisease.length > 0
      ) {
        const typeOfDiseaseArray = [...state.typeOfDisease];
        const extraQueryWithUndefined = typeOfDiseaseArray.includes('cantDefine');
        if (extraQueryWithUndefined) {
          // Can't define also means unknown and undefined types of disease
          typeOfDiseaseArray.push('unknown');
          query += `(${docType}@${f}.0.type€{exists==FALSE}|`;
        }
        query += `${docType}@${f}.0.type€${'$' + formatStringArray(typeOfDiseaseArray)}`;
        if (extraQueryWithUndefined) query += ')';
      }
      break;

    case 'patientAgeOnDg': // Get patient whose age was within the given numbers when selected diagnosis was received (diagnosis date)
      query = '';
      if (
        state.patientAgeOnDg !== 'noCropping' &&
        typeof state.patientAgeOnDg.minPatientAgeOnDg === 'number' &&
        typeof state.patientAgeOnDg.maxPatientAgeOnDg === 'number'
      ) {
        query +=
          `§patientAgeOnDg${getDiagnosisQueryString(state.platform, state.diagnosis)}` +
          `€{data>=#${state.patientAgeOnDg.minPatientAgeOnDg}&data<=#${state.patientAgeOnDg.maxPatientAgeOnDg}}`;
      }
      break;

    case 'relapseDuringLast24Months': {
      query = '';
      if (state.relapseDuringLast24Months !== 'noCropping') {
        if (state.relapseDuringLast24Months === 'yes') {
          query += `relapse@startDate>=%${partialDateToString(getPartialDateGivenMonthsAgo(24))}`;
        }
        if (state.relapseDuringLast24Months === 'no') {
          query += `!relapse@startDate>=%${partialDateToString(getPartialDateGivenMonthsAgo(24))}`;
        }
      }
      break;
    }

    case 'relapseDuringLast12Months':
      query = '';
      if (state.relapseDuringLast12Months !== 'noCropping') {
        if (state.relapseDuringLast12Months === 'yes') {
          query += `relapse@startDate>=%${partialDateToString(getPartialDateGivenMonthsAgo(12))}`;
        }
        if (state.relapseDuringLast12Months === 'no') {
          query += `!relapse@startDate>=%${partialDateToString(getPartialDateGivenMonthsAgo(12))}`;
        }
      }
      break;

    case 'numberOfRelapses':
      query = '';
      if (state.numberOfRelapses !== 'noCropping') {
        const d = getPartialDateGivenMonthsAgo(12);
        if (state.numberOfRelapses === 'more') {
          query += `§countDocsInDateRange[$${docType},%"${partialDateToString(d)}",NULL,$startDate]>#3`;
        } else {
          query += `§countDocsInDateRange[$${docType},%"${partialDateToString(d)}",NULL,$startDate]==#${
            state.numberOfRelapses
          }`;
        }
      }
      break;

    case 'edss':
      if (state.edss !== 'noCropping' && state.edss.length > 0) {
        query = '(';
        query += `§getLatestDocumentFieldValue[$${docType},$calculatedEdssStep,$date]€#[${state.edss
          .map(e => '#' + e.toString())
          .join(',')}]`;
        query += `|§getLatestDocumentFieldValue[$${docType},$edssStep,$date]€#[${state.edss
          .map(e => '#' + e.toString())
          .join(',')}])`;
      }
      break;

    case 'edssIncreasedDuringLast24Months':
    case 'edssIncreasedDuringLast12Months': {
      const months = f === 'edssIncreasedDuringLast24Months' ? 24 : 12;
      const s = months === 24 ? state.edssIncreasedDuringLast24Months : state.edssIncreasedDuringLast12Months;
      if (s !== 'noCropping') {
        if (s === 'yes') {
          query = '(';
          query += `§getLatestDocumentFieldValue[$${docType},$date,$date]>=%${partialDateToString(
            getPartialDateGivenMonthsAgo(months),
          )}`;
          query += `&(§calculateLatestFieldValueTrend[$${docType},$calculatedEdssStep,$calculatedEdssStep,$date]>#0`;
          query += `|§calculateLatestFieldValueTrend[$${docType},$calculatedEdssStep,$edssStep,$date]>#0`;
          query += `|§calculateLatestFieldValueTrend[$${docType},$edssStep,$calculatedEdssStep,$date]>#0`;
          query += `|§calculateLatestFieldValueTrend[$${docType},$edssStep,$edssStep,$date]>#0)`;
          query += ')';
        }
        if (s === 'no') {
          query = '(';
          query += `§getLatestDocumentFieldValue[$${docType},$date,$date]<%${partialDateToString(
            getPartialDateGivenMonthsAgo(months),
          )}`;
          query += `|(§calculateLatestFieldValueTrend[$${docType},$calculatedEdssStep,$calculatedEdssStep,$date]<=#0`;
          query += `&§calculateLatestFieldValueTrend[$${docType},$calculatedEdssStep,$edssStep,$date]<=#0`;
          query += `&§calculateLatestFieldValueTrend[$${docType},$edssStep,$calculatedEdssStep,$date]<=#0`;
          query += `&§calculateLatestFieldValueTrend[$${docType},$edssStep,$edssStep,$date]<=#0)`;
          query += ')';
        }
      }
      break;
    }

    case 'spmsCriteria':
      query = '';
      if (state.spmsCriteria !== 'noCropping') {
        if (state.spmsCriteria.includes('expand')) query += '§spmsModifiedExpand!=NULL';
        if (state.spmsCriteria.includes('expand') && state.spmsCriteria.includes('lorscheider')) query += '|';
        if (state.spmsCriteria.includes('lorscheider')) query += '§spmsLorscheider!=NULL';
      }
      break;

    default:
      return undefined;
  }

  return query;
};
